import { Camera, CheckSquare, ChevronRight, Eye, EyeOff, Repeat } from 'lucide-react';
import { Badge } from '@/components/atoms/badge';
import { Skeleton } from '@/components/atoms/skeleton';
import { ITEM_TYPE_LABEL, linksFromTemplate } from '@/lib/checklists/templateForm';
import { useCompanyTimezone } from '@/hooks/settings/useCompanyTimezone';
import { formatDateTimeInTimeZone } from '@/lib/date-values';
import { cn } from '@/lib/utils';
import type {
  ChecklistDefinitionVersion,
  ChecklistItemType,
  ChecklistTemplate,
} from '@/types/settings';

export interface ChecklistJobTypeSummary {
  id: string;
  name: string;
  color: string;
}

interface ChecklistTemplateReadOnlyBodyProps {
  template: ChecklistTemplate;
  jobTypes: ChecklistJobTypeSummary[];
  versions: ChecklistDefinitionVersion[];
  versionsLoading?: boolean;
  className?: string;
}

export function ChecklistVisibilityBadge({ template }: { template: ChecklistTemplate }) {
  const isCustomerVisible = template.defaultVisibility === 'CUSTOMER_VISIBLE';

  return (
    <Badge variant="outline" className="gap-1 text-[10px]">
      {isCustomerVisible ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
      {isCustomerVisible ? 'Customer' : 'Internal'}
    </Badge>
  );
}

export function ChecklistTemplateReadOnlyBody({
  template,
  jobTypes,
  versions,
  versionsLoading,
  className,
}: ChecklistTemplateReadOnlyBodyProps) {
  const companyTimeZone = useCompanyTimezone();
  const linkedJobTypes = linksFromTemplate(template)
    .map((link) => ({
      link,
      jobType: jobTypes.find((jobType) => jobType.id === link.jobTypeId),
    }))
    .filter((entry) => Boolean(entry.jobType));

  return (
    <div className={cn('space-y-5 rounded-xl border bg-card p-4 shadow-sm sm:p-6', className)}>
      <div className="grid gap-4 text-sm sm:grid-cols-2">
        <div>
          <p className="text-xs uppercase tracking-wide text-muted-foreground">
            Default Visibility
          </p>
          <Badge variant="outline" className="mt-1 text-[10px]">
            {template.defaultVisibility === 'CUSTOMER_VISIBLE' ? (
              <>
                <Eye className="mr-1 size-3" /> Customer-visible
              </>
            ) : (
              <>
                <EyeOff className="mr-1 size-3" /> Internal
              </>
            )}
          </Badge>
        </div>
        <div>
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Status</p>
          <Badge variant="outline" className="mt-1 text-[10px]">
            {template.status ?? 'ACTIVE'}
          </Badge>
        </div>
      </div>

      <div>
        <p className="text-xs uppercase tracking-wide text-muted-foreground">Linked Job Types</p>
        {linkedJobTypes.length === 0 ? (
          <p className="mt-1 text-xs italic text-amber-600">
            Not linked to any job type - won't auto-attach to appointments.
          </p>
        ) : (
          <div className="mt-1 flex flex-wrap gap-1.5">
            {linkedJobTypes.map(({ jobType, link }) => (
              <Badge key={link.jobTypeId} variant="outline">
                <span
                  className="mr-1.5 h-2 w-2 rounded-full"
                  style={{ backgroundColor: jobType?.color }}
                />
                {jobType?.name}
                {link.blocksCompletion ? ' · blocks completion' : ''}
              </Badge>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-3">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">Sections</p>
        {template.sections.map((section, sectionIndex) => (
          <div key={section.id ?? sectionIndex} className="rounded-md border">
            <div className="flex items-center gap-2 border-b bg-muted/30 px-3 py-2">
              <h4 className="text-sm font-semibold">{section.name}</h4>
              {section.isRepeatable && (
                <Badge variant="outline" className="text-[10px]">
                  <Repeat className="mr-1 size-2.5" />
                  Repeatable
                </Badge>
              )}
              <span className="ml-auto text-xs text-muted-foreground">
                {section.items.length} items
              </span>
            </div>
            {section.isRepeatable && section.repeatOptions && section.repeatOptions.length > 0 && (
              <div className="border-b px-3 py-1.5">
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Options</p>
                <div className="mt-1 flex flex-wrap gap-1">
                  {section.repeatOptions.map((option) => (
                    <Badge key={option} variant="secondary" className="text-[10px]">
                      {option}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            <ul className="divide-y">
              {section.items.map((item, itemIndex) => (
                <li key={item.id ?? itemIndex} className="flex items-start gap-2 px-3 py-2 text-sm">
                  <CheckSquare className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="font-medium">{item.label || <em>(untitled)</em>}</span>
                      <Badge variant="outline" className="text-[10px]">
                        {ITEM_TYPE_LABEL[item.type]}
                      </Badge>
                      {item.required && (
                        <Badge
                          variant="outline"
                          className="border-destructive/50 text-[10px] text-destructive"
                        >
                          Required
                        </Badge>
                      )}
                      {item.photoRequired && (
                        <Badge variant="outline" className="text-[10px]">
                          <Camera className="mr-1 size-2.5" />
                          Photo {item.minPhotos ?? 1}
                        </Badge>
                      )}
                    </div>
                    {item.description && (
                      <p className="mt-0.5 text-xs text-muted-foreground">{item.description}</p>
                    )}
                    {item.config && Object.keys(item.config).length > 0 && (
                      <p className="mt-0.5 text-[10px] text-muted-foreground">
                        {configSummary(item.type, item.config)}
                      </p>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="space-y-3">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">Version History</p>
        {versionsLoading ? (
          <Skeleton className="h-16 w-full" />
        ) : versions.length === 0 ? (
          <p className="text-xs text-muted-foreground">No version history yet.</p>
        ) : (
          <div className="divide-y rounded-md border">
            {versions.map((version) => (
              <details key={`${version.definitionId}-${version.version}`} className="group">
                <summary className="flex cursor-pointer list-none items-center gap-3 px-3 py-2 text-sm">
                  <Badge variant="outline" className="text-[10px]">
                    v{version.version}
                  </Badge>
                  <span className="font-medium">{version.name}</span>
                  <span className="ml-auto text-xs text-muted-foreground">
                    {formatVersionDate(version.createdAt, companyTimeZone)}
                  </span>
                  <ChevronRight className="size-3.5 text-muted-foreground transition-transform group-open:rotate-90" />
                </summary>
                <div className="space-y-2 border-t bg-muted/20 px-3 py-2">
                  {version.description && (
                    <p className="text-xs text-muted-foreground">{version.description}</p>
                  )}
                  {version.sections.map((section, sectionIndex) => (
                    <div
                      key={`${version.version}-${section.id ?? sectionIndex}`}
                      className="rounded border bg-background p-2"
                    >
                      <div className="flex items-center gap-2 text-xs font-medium">
                        <span>{section.name}</span>
                        <span className="ml-auto text-muted-foreground">
                          {section.items.length} items
                        </span>
                      </div>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {section.items.map((item, itemIndex) => (
                          <Badge
                            key={`${version.version}-${section.id ?? sectionIndex}-${item.id ?? itemIndex}`}
                            variant="secondary"
                            className="text-[10px]"
                          >
                            {item.label || '(untitled)'}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </details>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function formatVersionDate(value: string, timeZone?: string | null): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return formatDateTimeInTimeZone(value, timeZone, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

function configSummary(
  type: ChecklistItemType,
  config: NonNullable<ChecklistTemplate['sections'][number]['items'][number]['config']>,
): string {
  switch (type) {
    case 'NUMBER':
      return [
        config.min != null ? `min ${config.min}` : null,
        config.max != null ? `max ${config.max}` : null,
        config.unit ? `unit ${config.unit}` : null,
      ]
        .filter(Boolean)
        .join(' · ');
    case 'MEASUREMENT':
      return `units: ${(config.units ?? []).join(', ')}`;
    case 'DROPDOWN':
      return `options: ${(config.options ?? []).join(', ')}`;
    case 'SIGNATURE':
      return config.signerRole ? `signer: ${config.signerRole}` : '';
    case 'TEXT':
      return config.maxLength ? `max ${config.maxLength} chars` : '';
    case 'PHOTO':
      return [
        `min ${config.minPhotos ?? 1} photo(s)`,
        config.maxPhotos ? `max ${config.maxPhotos}` : null,
      ]
        .filter(Boolean)
        .join(' · ');
    case 'DATE':
      return [
        config.allowPast === false ? 'no past dates' : 'past dates allowed',
        config.allowFuture === false ? 'no future dates' : 'future dates allowed',
      ].join(' · ');
    case 'BARCODE_SCAN':
      return `format: ${config.format ?? 'ANY'}`;
    default:
      return '';
  }
}
